import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["disabled", "isActionSeparatorDisplayed", "error", "label", "onSelect", "onRenderSelectedValue", "options", "onChange", "multiple", "formatOption", "enableSelectAllOption", "selectAllLabel", "enableSearchOption", "searchPlaceholder", "defaultSearchQuery", "tagLabelMaxLength", "openTagsCount", "renderAggregatedTagsLabel", "singleLine", "open", "defaultOpen", "onOpenChange", "isListExpanded", "autoFocus", "defaultValue", "name", "placeholder", "value", "tabIndex"];
import { useCallback, useMemo, useRef } from 'react';
import { noop, useDetectClickOutsideComponent, useDetectEscapeKeyPressed, useInternalRef, useOnValueUpdate, useOptions, useSelectedValueToDisplay, useValue } from '../../../utils';
import { useControlledState } from '../../../system/hooks/useControlledState';
import { useGuidedState } from '../../../system/hooks/useGuidedState';
import { useOnOptionListUpdate } from './useOnOptionListUpdate';
export var useDropdown = function useDropdown(_ref, ref) {
  var disabled = _ref.disabled,
      _ref$isActionSeparato = _ref.isActionSeparatorDisplayed,
      isActionSeparatorDisplayed = _ref$isActionSeparato === void 0 ? true : _ref$isActionSeparato,
      error = _ref.error,
      label = _ref.label,
      onSelect = _ref.onSelect,
      onRenderSelectedValue = _ref.onRenderSelectedValue,
      options = _ref.options,
      onChange = _ref.onChange,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      formatOption = _ref.formatOption,
      enableSelectAllOption = _ref.enableSelectAllOption,
      selectAllLabel = _ref.selectAllLabel,
      enableSearchOption = _ref.enableSearchOption,
      searchPlaceholder = _ref.searchPlaceholder,
      defaultSearchQuery = _ref.defaultSearchQuery,
      tagLabelMaxLength = _ref.tagLabelMaxLength,
      _ref$openTagsCount = _ref.openTagsCount,
      openTagsCount = _ref$openTagsCount === void 0 ? 0 : _ref$openTagsCount,
      renderAggregatedTagsLabel = _ref.renderAggregatedTagsLabel,
      singleLine = _ref.singleLine,
      open = _ref.open,
      defaultOpen = _ref.defaultOpen,
      onOpenChange = _ref.onOpenChange,
      _ref$isListExpanded = _ref.isListExpanded,
      isListExpanded = _ref$isListExpanded === void 0 ? false : _ref$isListExpanded,
      autoFocus = _ref.autoFocus,
      defaultValue = _ref.defaultValue,
      name = _ref.name,
      placeholder = _ref.placeholder,
      value = _ref.value,
      tabIndex = _ref.tabIndex,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var internalInputRef = useRef(null);
  var containerRef = useRef(null);
  useInternalRef(ref, internalInputRef);

  var _useValue = useValue(value, defaultValue),
      _useValue2 = _slicedToArray(_useValue, 2),
      internalValue = _useValue2[0],
      setInternalValue = _useValue2[1];

  var _useOptions = useOptions(options, formatOption),
      internalOptions = _useOptions.internalOptions,
      isFlat = _useOptions.isFlat;

  var _useGuidedState = useGuidedState(function () {
    return internalOptions;
  }, [internalOptions]),
      _useGuidedState2 = _slicedToArray(_useGuidedState, 2),
      dynamicInternalOptions = _useGuidedState2[0],
      setDynamicInternalOptions = _useGuidedState2[1];

  useOnOptionListUpdate(internalOptions, dynamicInternalOptions, setInternalValue);
  var onValueUpdate = useOnValueUpdate(setInternalValue, onSelect, onChange, name);
  var selectedValueToDisplay = useSelectedValueToDisplay(internalValue, internalOptions, onRenderSelectedValue);

  var _useControlledState = useControlledState(isListExpanded !== undefined ? isListExpanded : defaultOpen, open, onOpenChange),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      isOptionListShown = _useControlledState2[0],
      setIsOptionListShown = _useControlledState2[1];

  var blurInput = useCallback(function () {
    var node = internalInputRef.current;

    if (node && node.blur) {
      setTimeout(function () {
        return node.blur();
      }, 0);
    }
  }, [internalInputRef]);

  var focusOnActionItemTrigger = function focusOnActionItemTrigger() {
    var node = internalInputRef.current;

    if (!isOptionListShown) {
      if (node && node.focus) {
        node.focus();
      }
    } else {
      blurInput();
    }
  };

  useDetectClickOutsideComponent(containerRef, setIsOptionListShown, isOptionListShown);
  useDetectEscapeKeyPressed(containerRef, setIsOptionListShown, isOptionListShown);
  var handleDisplayOptionListToggle = useCallback(function () {
    setIsOptionListShown(!isOptionListShown);
    focusOnActionItemTrigger();
  }, [setIsOptionListShown, isOptionListShown]);
  var handleExpansionClick = useMemo(function () {
    return disabled ? noop : handleDisplayOptionListToggle;
  }, [disabled, isOptionListShown]);
  return {
    rootProps: restProps,
    containerProps: {
      ref: containerRef
    },
    textInputProps: {
      onClick: handleExpansionClick,
      ref: internalInputRef,
      value: selectedValueToDisplay,
      internalValue: internalValue,
      disabled: disabled,
      error: error,
      label: label,
      name: name,
      displayMode: 'block',
      isCustomRenderer: !!onRenderSelectedValue,
      autoComplete: 'off',
      readOnly: true,
      autoFocus: autoFocus,
      placeholder: placeholder,
      tabIndex: tabIndex,
      multiple: multiple
    },
    optionListProps: {
      disabled: disabled,
      options: options,
      multiple: multiple,
      internalValue: internalValue,
      blurInput: blurInput,
      isOptionListShown: isOptionListShown,
      setInternalValue: setInternalValue,
      setIsOptionListShown: setIsOptionListShown,
      onValueUpdate: onValueUpdate,
      onChange: onChange,
      isFlat: isFlat,
      internalOptions: internalOptions,
      dynamicInternalOptions: dynamicInternalOptions,
      setInternalOptions: setDynamicInternalOptions,
      enableSelectAllOption: enableSelectAllOption,
      selectAllLabel: selectAllLabel,
      enableSearchOption: enableSearchOption,
      searchPlaceholder: searchPlaceholder,
      defaultSearchQuery: defaultSearchQuery
    },
    dropdownExpansionIndicatorProps: {
      disabled: disabled,
      error: error,
      multiple: multiple,
      displaySeparator: !multiple && isActionSeparatorDisplayed && (isOptionListShown || !!internalValue.length),
      isExpanded: isOptionListShown,
      onClick: handleExpansionClick
    },
    dropdownBeforeSectionProps: {
      disabled: disabled,
      onClick: handleExpansionClick,
      internalValue: internalValue,
      internalOptions: internalOptions,
      multiple: multiple,
      onRenderSelectedValue: onRenderSelectedValue,
      setInternalValue: setInternalValue,
      onSelect: onSelect,
      onChange: onChange,
      options: options,
      isFlat: isFlat,
      tagLabelMaxLength: tagLabelMaxLength,
      openTagsCount: openTagsCount,
      renderAggregatedTagsLabel: renderAggregatedTagsLabel,
      singleLine: singleLine
    },
    disabled: disabled,
    multiple: multiple,
    onRenderSelectedValue: onRenderSelectedValue,
    internalValue: internalValue
  };
};